import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useInView } from "react-intersection-observer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMountain, faHome, faLeaf } from "@fortawesome/free-solid-svg-icons";
import logo1 from "../assets/logo1.png";
import logo2 from "../assets/logo2.png";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const AboutSection = styled.section`
  padding: 120px 0;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, #1a5f7a, #159895);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, #159895, #1a5f7a);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-size: 3.2rem;
  color: #1a5f7a;
  text-align: center;
  margin-bottom: 20px;
  font-weight: 800;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeInUp} 0.8s ease-out forwards;
  background: linear-gradient(135deg, #1a5f7a, #159895);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.3rem;
  color: #666;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 80px;
  line-height: 1.8;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeInUp} 0.8s ease-out 0.2s forwards;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 50px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: white;
  padding: 40px 30px;
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeInUp} 0.8s ease-out ${(props) => props.delay}s forwards;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #1a5f7a, #159895);
    transform: scaleX(0);
    transition: transform 0.4s ease;
  }

  &:hover {
    transform: translateY(-15px);
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.1);

    &::before {
      transform: scaleX(1);
    }

    ${(props) => props.icon} {
      transform: scale(1.1);
    }
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #1a5f7a, #159895);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  color: white;
  font-size: 2.5rem;
  box-shadow: 0 10px 20px rgba(26, 95, 122, 0.15);
  transition: all 0.4s ease;
  animation: ${float} 6s ease-in-out infinite;
`;

const FeatureTitle = styled.h3`
  font-size: 1.6rem;
  color: #1a5f7a;
  margin-bottom: 15px;
  font-weight: 700;
`;

const FeatureDescription = styled.p`
  color: #666;
  line-height: 1.8;
  font-size: 1.1rem;
`;

const ExperienceShowcase = styled.div`
  margin-top: 100px;
  padding-top: 80px;
  border-top: 1px solid rgba(26, 95, 122, 0.1);
`;

const LogosContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 80px;
  margin-top: 40px;
  flex-wrap: wrap;
`;

const ExperienceLogo = styled.img`
  height: ${(props) => props.height || "120px"};
  transition: all 0.3s ease;
  transform: ${(props) => (props.scale ? `scale(${props.scale})` : "none")};

  &:hover {
    transform: ${(props) =>
      props.scale ? `scale(${parseFloat(props.scale) + 0.05})` : "scale(1.05)"};
  }

  @media (max-width: 768px) {
    height: ${(props) => (props.height ? "100px" : "110px")};
  }

  @media (max-width: 576px) {
    height: ${(props) => (props.height ? "90px" : "100px")};
    margin: 10px 0;
  }
`;

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <AboutSection id="about" ref={ref}>
      <Container>
        <SectionTitle>Why Choose Skyline Terrace?</SectionTitle>
        <SectionSubtitle>
          Discover the perfect blend of luxury, comfort, and natural beauty in
          our premium residential community
        </SectionSubtitle>

        <FeaturesGrid>
          <FeatureCard delay={0.3}>
            <FeatureIcon>🏔️</FeatureIcon>
            <FeatureTitle>Breathtaking Views</FeatureTitle>
            <FeatureDescription>
              Wake up to stunning panoramic views of the Galiyat mountains,
              creating a serene and peaceful living environment.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard delay={0.4}>
            <FeatureIcon>🏊</FeatureIcon>
            <FeatureTitle>Premium Amenities</FeatureTitle>
            <FeatureDescription>
              Enjoy world-class facilities including a swimming pool, fitness
              center, and beautifully landscaped gardens.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard delay={0.5}>
            <FeatureIcon>🔒</FeatureIcon>
            <FeatureTitle>24/7 Security</FeatureTitle>
            <FeatureDescription>
              Your safety is our priority with round-the-clock security and
              modern surveillance systems.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard delay={0.6}>
            <FeatureIcon>🌳</FeatureIcon>
            <FeatureTitle>Natural Surroundings</FeatureTitle>
            <FeatureDescription>
              Immerse yourself in nature with our carefully preserved green
              spaces and walking trails.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard delay={0.7}>
            <FeatureIcon>🏠</FeatureIcon>
            <FeatureTitle>Modern Living</FeatureTitle>
            <FeatureDescription>
              Experience contemporary living with thoughtfully designed spaces
              and high-quality finishes.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard delay={0.8}>
            <FeatureIcon>🤝</FeatureIcon>
            <FeatureTitle>Community Living</FeatureTitle>
            <FeatureDescription>
              Join a vibrant community of like-minded individuals in a welcoming
              and friendly environment.
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>

        <ExperienceShowcase>
          <SectionTitle>Our Experiences</SectionTitle>
          <SectionSubtitle>
            Skyline Terrace is a newly launched project, committed to delivering
            exceptional living spaces to families across Pakistan
          </SectionSubtitle>
          <LogosContainer>
            <ExperienceLogo
              src={logo1}
              alt="Experience Logo 1"
              height="140px"
              scale="1.5"
            />
            <ExperienceLogo
              src={logo2}
              alt="Experience Logo 2"
              height="110px"
            />
          </LogosContainer>
        </ExperienceShowcase>
      </Container>
    </AboutSection>
  );
};

export default About;
